import { LoadingOutlined, RobotOutlined } from '@ant-design/icons';
import { Alert, Space } from 'antd';
import { useState } from 'react';
import { reviewText } from '@/features/inventorization/review-i18n';
import type { CaptureSession } from '@/shared/capture/capture-session';
import { INVENTORIZE_EXTRACTION_BACKEND_STATUSES } from '@/shared/ocr/inventorize-extraction-contract';
import {
  getLatestCompletedInventorizeExtraction,
  getLatestInventorizeExtractionJob,
  isServerInventorizeExtractionJobOutput,
} from '@/shared/ocr/inventorize-extraction-jobs';

interface ReviewExtractionAlertsProps {
  error: string | null;
  isPolling: boolean;
  session: CaptureSession;
}

export function ReviewExtractionAlerts({ error, isPolling, session }: ReviewExtractionAlertsProps) {
  const latestJob = getLatestInventorizeExtractionJob(session.aiExtractions);
  const completedExtraction = getLatestCompletedInventorizeExtraction(session.aiExtractions);
  const job = latestJob && isServerInventorizeExtractionJobOutput(latestJob.output) ? latestJob.output : undefined;
  const warnings = completedExtraction?.warnings ?? [];
  const dismissedAlertKeys = useDismissedReviewAlerts(session.id);

  if (!(job || error)) {
    return null;
  }

  if (error) {
    const key = `extraction-error:${error}`;

    return dismissedAlertKeys.keys.has(key) ? null : (
      <Alert closable message={error} onClose={() => dismissedAlertKeys.dismiss(key)} showIcon type="warning" />
    );
  }

  if (!job) {
    return null;
  }

  if (job.backendStatus === INVENTORIZE_EXTRACTION_BACKEND_STATUSES.failed) {
    const message = job.error?.message ?? reviewText('extraction.failed', 'AI extraction failed.');
    const key = `job-failed:${job.jobId}:${message}`;

    return dismissedAlertKeys.keys.has(key) ? null : (
      <Alert closable message={message} onClose={() => dismissedAlertKeys.dismiss(key)} showIcon type="error" />
    );
  }

  if (
    isPolling ||
    job.backendStatus === INVENTORIZE_EXTRACTION_BACKEND_STATUSES.created ||
    job.backendStatus === INVENTORIZE_EXTRACTION_BACKEND_STATUSES.running
  ) {
    const key = `job-running:${job.jobId}`;

    if (dismissedAlertKeys.keys.has(key)) {
      return null;
    }

    return (
      <Alert
        closable
        icon={<LoadingOutlined />}
        message={reviewText('extraction.runningTitle', 'AI extraction is running')}
        onClose={() => dismissedAlertKeys.dismiss(key)}
        description={reviewText(
          'extraction.runningDescription',
          'You can review manual fields now. AI suggestions will appear here when the job completes.',
        )}
        showIcon
        type="info"
      />
    );
  }

  const completeKey = `job-complete:${job.jobId}`;
  const showCompleteAlert = !dismissedAlertKeys.keys.has(completeKey);
  const visibleWarnings = warnings.filter(warning => !dismissedAlertKeys.keys.has(`warning:${warning}`));

  if (!(showCompleteAlert || visibleWarnings.length > 0)) {
    return null;
  }

  return (
    <Space direction="vertical" size="small" style={{ width: '100%' }}>
      {showCompleteAlert ? (
        <Alert
          closable
          icon={<RobotOutlined />}
          message={reviewText('extraction.complete', 'AI extraction complete')}
          onClose={() => dismissedAlertKeys.dismiss(completeKey)}
          showIcon
          type="success"
        />
      ) : null}
      {visibleWarnings.map(warning => {
        const key = `warning:${warning}`;

        return (
          <Alert
            closable
            key={warning}
            message={warning}
            onClose={() => dismissedAlertKeys.dismiss(key)}
            showIcon
            type="warning"
          />
        );
      })}
    </Space>
  );
}

function useDismissedReviewAlerts(sessionId: string) {
  const storageKey = `enmon-inventory-dismissed-review-alerts:${sessionId}`;
  const [keys, setKeys] = useState(() => readDismissedAlertKeys(storageKey));

  function dismiss(key: string) {
    setKeys(current => {
      const next = new Set(current);
      next.add(key);
      writeDismissedAlertKeys(storageKey, next);

      return next;
    });
  }

  return { dismiss, keys };
}

function readDismissedAlertKeys(storageKey: string) {
  if (typeof globalThis.sessionStorage === 'undefined') {
    return new Set<string>();
  }

  try {
    const parsed = JSON.parse(globalThis.sessionStorage.getItem(storageKey) ?? '[]');

    return new Set(Array.isArray(parsed) ? parsed.filter((value): value is string => typeof value === 'string') : []);
  } catch {
    return new Set<string>();
  }
}

function writeDismissedAlertKeys(storageKey: string, keys: Set<string>) {
  if (typeof globalThis.sessionStorage === 'undefined') {
    return;
  }

  globalThis.sessionStorage.setItem(storageKey, JSON.stringify([...keys]));
}
